import posthog from "posthog-js";
import {
  type CaptureErrorContext,
  type ErrorLevel,
  normalizeUnknownError,
  toCaptureErrorContext,
  toCaptureProperties,
} from "./error-tracking/capture-properties";
import { addBreadcrumb, type Breadcrumb } from "./error-tracking/breadcrumbs";
import { resolveTelemetryEnvironment } from "./telemetry-runtime";

export type { CaptureErrorContext, ErrorLevel };
export type { Breadcrumb };
export { addBreadcrumb };

function getPosthogSessionExtra(): Record<string, string> {
  try {
    const sessionId =
      typeof posthog.get_session_id === "function"
        ? posthog.get_session_id()
        : undefined;
    const replayUrl =
      typeof posthog.get_session_replay_url === "function"
        ? posthog.get_session_replay_url({ withTimestamp: true })
        : undefined;
    const out: Record<string, string> = {};
    if (sessionId) out.posthog_session_id = sessionId;
    if (replayUrl) out.posthog_session_replay_url = replayUrl;
    return out;
  } catch {
    return {};
  }
}

export function captureError(
  error: unknown,
  context?: CaptureErrorContext | Record<string, unknown>,
): string | undefined {
  if (resolveTelemetryEnvironment() === "development") return undefined;

  const normalized = normalizeUnknownError(error);
  const captureContext = toCaptureErrorContext(context);

  const properties = toCaptureProperties(captureContext, {
    ...normalized.extra,
    ...getPosthogSessionExtra(),
  });

  try {
    const result = posthog.captureException(normalized.error, properties);

    return result?.uuid;
  } catch {
    return undefined;
  }
}

export function captureMessage(
  message: string,
  levelOrOptions: ErrorLevel | CaptureErrorContext = "info",
) {
  const context =
    typeof levelOrOptions === "string"
      ? { level: levelOrOptions }
      : levelOrOptions;

  captureError(new Error(message), {
    ...context,
    level: context.level ?? "info",
    fingerprint: context.fingerprint ?? [message],
  });
}

function registerTenantProperty(key: string, value: string | undefined) {
  try {
    if (value === undefined) {
      posthog.unregister(key);
      return;
    }
    posthog.register({ [key]: value });
  } catch {
  }
}

export function setUser(
  user: {
    id?: string;
    email?: string;
    username?: string;
  } | null,
) {
  registerTenantProperty("user_id", user?.id ?? undefined);
}

export function setCompanyContext(
  company: {
    id: string;
    name?: string;
    accountType?: string;
  } | null,
) {
  registerTenantProperty("company_id", company?.id);
  registerTenantProperty("company_account_type", company?.accountType);
  registerTenantProperty("company_name", company?.name);
}

export function setRoleContext(role: string | null) {
  registerTenantProperty("user_role", role ?? undefined);
}

export function getSessionReplayUrl(): string | undefined {
  try {
    if (typeof posthog.get_session_replay_url !== "function") return undefined;
    const url = posthog.get_session_replay_url({ withTimestamp: true });
    return typeof url === "string" && url.length > 0 ? url : undefined;
  } catch {
    return undefined;
  }
}
